"use client";

import type React from "react";
import { useState, useCallback } from "react";
import {
  FormControl,
  FormLabel,
  Switch,
  Text,
  useToast,
  Box,
  Flex,
} from "@chakra-ui/react";
import { agentService } from "../../../voice-services/agentService";

interface OptOutSensitiveDataProps {
  agentId: string;
  initialOptOut: boolean;
}

export const OptOutSensitiveData: React.FC<OptOutSensitiveDataProps> = ({
  agentId,
  initialOptOut,
}) => {
  const [optOut, setOptOut] = useState(initialOptOut);
  const [isUpdating, setIsUpdating] = useState(false);
  const toast = useToast();

  const updateOptOut = async (checked: boolean) => {
    setIsUpdating(true);
    try {
      // ✅ Retell replacement:
      // - opt out = basic_attributes_only
      // - opt in  = everything (default)
      await agentService.updateAgentSettings(agentId, {
        data_storage_setting: checked ? "basic_attributes_only" : "everything",
      });

      setOptOut(checked);
      toast({
        title: "Sensitive data storage preference updated",
        status: "success",
        duration: 2000,
      });
    } catch (error: any) {
      console.error("Error updating sensitive data preference:", error);
      toast({
        title: "Error updating preference",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 3000,
      });
      // Revert UI state on error
      setOptOut(!checked);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggle = useCallback(async () => {
    if (isUpdating) return;
    updateOptOut(!optOut);
  }, [optOut, isUpdating]);

  return (
    <FormControl>
      <Flex justify="space-between" align="center">
        <Box>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.700" mb={1}>
            Opt Out Sensitive Data Storage
          </FormLabel>
          <Text fontSize="xs" color="gray.600">
            Control whether we should store transcripts/recordings/logs. Turning
            this on stores only basic attributes.
          </Text>
        </Box>
        <Switch
          isChecked={optOut}
          onChange={handleToggle}
          isDisabled={isUpdating}
          colorScheme="primary"
        />
      </Flex>
    </FormControl>
  );
};
